import {
  type ApplyUpdateOutcome,
  type ApplyUpdateResponse,
  ExtensionMessageType,
} from "@punto/frontend-shared/extension";

import {
  type ExtensionConnection,
  extensionConnection,
} from "./extension-connection";
import { sendExtensionMessage } from "./send-extension-message";

/** How long the extension has to answer APPLY_UPDATE. */
const APPLY_UPDATE_TIMEOUT_MS = 2_000;
/** How long a reloading extension has to come back with its new version. */
const NEW_VERSION_TIMEOUT_MS = 10_000;

const requestApplyUpdate = (): Promise<ApplyUpdateOutcome | undefined> =>
  new Promise((resolve) => {
    const timer = setTimeout(() => resolve(undefined), APPLY_UPDATE_TIMEOUT_MS);
    const sent = sendExtensionMessage(
      { type: ExtensionMessageType.APPLY_UPDATE },
      (response) => {
        clearTimeout(timer);
        resolve((response as ApplyUpdateResponse | undefined)?.outcome);
      },
    );
    if (!sent) {
      clearTimeout(timer);
      resolve(undefined);
    }
  });

/**
 * What "Actualizar" does before Janus reloads (P-2789): asks a connected
 * extension to install the version Chrome downloaded, and on `reloading`
 * waits for it to come back — a page reloaded before then would load without
 * the content script. Always resolves, so the caller always reloads.
 */
export async function applyExtensionUpdate(
  connection: Pick<
    ExtensionConnection,
    "getSnapshot" | "waitForVersionOtherThan"
  > = extensionConnection,
): Promise<void> {
  const { state, version } = connection.getSnapshot();
  if (state !== "connected") return;

  const outcome = await requestApplyUpdate();
  if (outcome !== "reloading") return;

  await connection.waitForVersionOtherThan(version, NEW_VERSION_TIMEOUT_MS);
}
